import { useEventCallback } from '@mui/material/utils';
import { noop } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import {
  IStackItem,
  StackProvider,
} from '@work4all/components/lib/navigation/history-stack';
import { Content } from '@work4all/components/lib/navigation/navigation-overlay/components/content/Content';

import { OverlayFullscreenContext } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTableView } from '../containers/file-entities-lists';
import { ContentWrapper } from '../containers/file-entities-lists/components/contentWrapper/ContentWrapper';
import { OpenMaskOverride } from '../containers/mask-overlays/mask-overlay/hooks/use-open-mask';

import { MaskDialogOverlay } from './entity-picker/MaskDialogOverlay';

type Props = {
  open: boolean;
  entity: Entities;
  close: () => void;
};

export const EntityTableViewStack = ({ entity, open, close }: Props) => {
  const dialogs = useDialogs();
  const { t } = useTranslation();

  const openMaskHandler = useEventCallback(() => {
    return {
      handler: (config) => {
        const dialog = dialogs.open(MaskDialogOverlay, {
          id: config?.id,
          entity,
          onAfterSave: close,
          close,
        });

        function close() {
          dialogs.close(dialog.id);
        }
      },
    };
  });

  const initialView: IStackItem = useMemo(() => {
    const view = (
      <OpenMaskOverride value={{ onOpenMask: openMaskHandler }}>
        <ContentWrapper
          title={t(`COMMON.${entity.toUpperCase()}`)}
          entity={entity}
          backAction="back"
          amplitudeEntryPoint="entity-table-view-stack"
          onBack={close}
        >
          <EntityTableView entityType={entity} />
        </ContentWrapper>
      </OpenMaskOverride>
    );

    return { view };
  }, [openMaskHandler, t, entity, close]);

  return (
    <OverlayFullscreenContext.Provider
      value={{ fullscreen: true, setFullscreen: noop, setSettingsKey: noop }}
    >
      <StackProvider onClose={close} initialView={initialView}>
        <Content open={open} close={close} onExited={close} />
      </StackProvider>
    </OverlayFullscreenContext.Provider>
  );
};
